import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslatedContent } from '../i18n/hooks/useTranslatedContent';
import ContactForm from './contact/ContactForm';
import { COMPANY_CONTACT } from '../constants/contact';

const Contact = () => {
  const { t } = useTranslatedContent('contact');

  return (
    <div id="contact" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-600/25 rounded-full filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-cyan-500/20 rounded-full filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-emerald-500/15 rounded-full filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">{t('title')}</h2>
          <p className="text-lg text-slate-400">
            {t('subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="glass-card rounded-3xl p-8">
            <ContactForm />
          </div>

          {/* Contact Information */}
          <div className="glass-card rounded-3xl p-8">
            <div className="space-y-8">
              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-cyan-400/10 border border-cyan-400/20 rounded-xl group-hover:bg-cyan-400/20 transition-colors duration-300">
                    <Mail className="h-6 w-6 text-cyan-300" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.email')}</h3>
                  <a 
                    href={`mailto:${COMPANY_CONTACT.email}`} 
                    className="text-slate-300 hover:text-cyan-200 transition-colors duration-300"
                  >
                    {COMPANY_CONTACT.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-cyan-400/10 border border-cyan-400/20 rounded-xl group-hover:bg-cyan-400/20 transition-colors duration-300">
                    <Phone className="h-6 w-6 text-cyan-300" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.phone')}</h3>
                  <a 
                    href={`tel:${COMPANY_CONTACT.phone.replace(/\s/g, '')}`} 
                    className="text-slate-300 hover:text-cyan-200 transition-colors duration-300"
                  >
                    {COMPANY_CONTACT.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-cyan-400/10 border border-cyan-400/20 rounded-xl group-hover:bg-cyan-400/20 transition-colors duration-300">
                    <MapPin className="h-6 w-6 text-cyan-300" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.location')}</h3>
                  <p className="text-slate-300">
                    {COMPANY_CONTACT.address.street}<br />
                    {COMPANY_CONTACT.address.postalCode} {COMPANY_CONTACT.address.city}<br />
                    {COMPANY_CONTACT.address.country}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;